import { defineRouting } from "next-intl/routing";

export type FullRoute = string | TranslatedRoute

export type TranslatedRoute = {
  en: string,
  nl: string
}

export const routing = defineRouting({
  locales: ["nl", "en"],
  defaultLocale: "nl",

  pathnames: {
    "/": "/",

    "/auth": "/auth",
    "/auth/error": "/auth/error",
    "/auth/forgot-password": {
      en: "/auth/forgot-password",
      nl: "/auth/wachtwoord-vergeten",
    },
    "/auth/setup-password": {
      en: "/auth/setup-password",
      nl: "/auth/wachtwoord-aanmaken",
    },
    "/auth/sign-in": {
      en: "/auth/sign-in",
      nl: "/auth/inloggen",
    },
    "/auth/verify-request": {
      en: "/auth/verify-request",
      nl: "/auth/email-verificatie",
    },

    "/explore/business-unit/[id]": {
      en: "/explore/business-unit/[id]",
      nl: "/ontdek/afdeling/[id]",
    },
    "/explore/business-units": {
      en: "/explore/business-units",
      nl: "/ontdek/afdelingen",
    },
    "/explore/categories": {
      en: "/explore/categories",
      nl: "/ontdek/categorieen",
    },
    "/explore/category/[id]": {
      en: "/explore/category/[id]",
      nl: "/ontdek/categorie/[id]",
    },
    "/explore/companies": {
      en: "/explore/companies",
      nl: "/ontdek/leveranciers",
    },
    "/explore/company/[id]": {
      en: "/explore/company/[id]",
      nl: "/ontdek/leverancier/[id]",
    },
    "/explore/expenses": {
      en: "/explore/expenses",
      nl: "/ontdek/uitgaven",
    },
    "/explore/invoice/[id]": {
      en: "/explore/invoice/[id]",
      nl: "/ontdek/factuur/[id]",
    },
    "/explore/order/[id]": {
      en: "/explore/order/[id]",
      nl: "/ontdek/bestelling/[id]",
    },
    "/explore/orders": {
      en: "/explore/orders",
      nl: "/ontdek/order",
    },
    "/import": {
      en: "/import",
      nl: "/importeren",
    },
    "/import/[cuid]": {
      en: "/import/[cuid]",
      nl: "/importeren/[cuid]",
    },
    "/import/[cuid]/commit": {
      en: "/import/[cuid]/commit",
      nl: "/importeren/[cuid]/bevestigen",
    },
    "/import/[cuid]/fetch-addresses": {
      en: "/import/[cuid]/fetch-addresses",
      nl: "/importeren/[cuid]/adressen-ophalen",
    },
    "/import/[cuid]/map": {
      en: "/import/[cuid]/map",
      nl: "/importeren/[cuid]/kolommen",
    },
    "/import/[cuid]/merge": {
      en: "/import/[cuid]/merge",
      nl: "/importeren/[cuid]/samenvoegen",
    },
    "/import/[cuid]/stage": {
      en: "/import/[cuid]/stage",
      nl: "/importeren/[cuid]/staging",
    },
    "/import/[cuid]/upload": {
      en: "/import/[cuid]/upload",
      nl: "/importeren/[cuid]/uploaden",
    },
    "/import/[cuid]/validation": {
      en: "/import/[cuid]/validation",
      nl: "/importeren/[cuid]/validatie",
    },
    "/import/groups": {
      en: "/import/groups",
      nl: "/importeren/groepen",
    },
    "/import/groups/[groupId]": {
      en: "/import/groups/[groupId]",
      nl: "/importeren/groepen/[groupId]",
    },
    "/import/start": {
      en: "/import/start",
      nl: "/importeren/start",
    },

    "/manage/users": {
      en: "/manage/users",
      nl: "/beheer/gebruikers",
    },

    "/map": {
      en: "/map",
      nl: "/kaart",
    },

    "/search": {
      en: "/search",
      nl: "/zoeken",
    },
    
    "/organization/business-units": {
      en: "/organization/business-units",
      nl: "/organisatie/afdelingen",
    },

    "/organization/users": {
      en: "/organization/users",
      nl: "/organisatie/gebruikers",
    },

    "/workbooks/[slug]": "/workbooks/[slug]",
    "/workbooks/[slug]/editor": "/workbooks/[slug]/editor",
  },
});

type StaticPathname<T> = T extends `${string}[${string}]${string}` ? never : T;
type ParameteredPathname<T> = T extends `${string}[${string}]${string}` ? T : never;
type ExtractParams<T extends string> = T extends `${string}[${infer Param}]${infer Rest}` ? Param | ExtractParams<Rest> : never;

export type AppPathnames = keyof typeof routing.pathnames;
export type AppStaticPathnames = StaticPathname<AppPathnames>;
export type AppParameteredPathnames = ParameteredPathname<AppPathnames>;
export type AppTypedRoutes =
  | {
    [P in AppPathnames]: (ExtractParams<P> extends never
      ? P // Static route: just the string
      : {
        pathname: P;
        params: { [K in ExtractParams<P>]: string | number };
      }) & {
        hash?: string;
        query?: Record<string, string | number | boolean | undefined>;
      };
  }[AppPathnames]
  | AppStaticPathnames;

/**
 * Returns the original pathname key and its translated object/string for a given localized URL path.
 * Example: "/nl/auth/profiel-afmaken" -> { key: "/auth/complete-profile", value: { en: "...", nl: "..." } }
 */
export function findPathnameMetadata(pathname: string) {
  const locales = routing.locales;
  let pathWithoutLocale = pathname;

  // 1. Strip locale prefix
  for (const locale of locales) {
    if (pathname.startsWith(`/${locale}/`)) {
      pathWithoutLocale = pathname.substring(locale.length + 1);
      break;
    } else if (pathname === `/${locale}`) {
      pathWithoutLocale = "/";
      break;
    }
  }

  // Ensure it starts with /
  if (!pathWithoutLocale.startsWith("/")) {
    pathWithoutLocale = "/" + pathWithoutLocale;
  }

  // 2. Find in routing.pathnames
  for (const [key, value] of Object.entries(routing.pathnames)) {
    if (typeof value === "string") {
      if (value === pathWithoutLocale) {
        return { key: key as AppPathnames, value };
      }
    } else {
      const translated = value as TranslatedRoute;
      if (translated.en === pathWithoutLocale || translated.nl === pathWithoutLocale) {
        return { pathNameKey: key as AppPathnames, translatedValue: translated };
      }
    }
  }

  return null;
}
